import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { theme } from '../theme';
import { assetUrl } from '../utils/assetUrl';

const ROW_HEIGHT_PX = 2;

const Tile = styled.button`
  appearance: none;
  border: none;
  background: ${theme.colors.gallery};
  border-radius: 0;
  padding: 0;
  margin: 0;
  cursor: pointer;
  text-align: left;
  overflow: hidden;
  transition: opacity 0.15s ease;
  position: relative;
  display: block;
  width: 100%;
  grid-row-end: span ${({ $rowSpan }) => $rowSpan || 1};

  &:hover {
    opacity: 0.92;
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.accent};
    outline-offset: -2px;
    z-index: 1;
  }
`;

const Thumb = styled.div`
  position: relative;
  line-height: 0;
  background: ${theme.colors.gallery};
`;

const Img = styled.img`
  width: 100%;
  height: auto;
  display: block;
  image-rendering: pixelated;
  image-rendering: crisp-edges;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(44, 40, 37, 0.78);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.space[2]}px;
  opacity: 0;
  transition: opacity 0.15s ease;

  ${Tile}:hover &,
  ${Tile}:focus-visible & {
    opacity: 1;
  }
`;

const OverlayTitle = styled.span`
  color: #fff;
  font-family: ${theme.fonts.display};
  font-size: ${theme.fontSizes.md};
  font-weight: normal;
  text-align: center;
  line-height: 1.3;
`;

export function ArtTile({ title, imageUrl, onClick }) {
  const imgRef = useRef(null);
  const [rowSpan, setRowSpan] = useState(1);

  const updateSpan = useCallback(() => {
    const img = imgRef.current;
    if (!img) return;

    // Use offsetHeight for stable pixel measurements.
    const h = img.offsetHeight || img.getBoundingClientRect().height || 0;
    const span = Math.max(1, Math.ceil((h + 1) / ROW_HEIGHT_PX));
    setRowSpan(span);
  }, []);

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return undefined;

    const ro = new ResizeObserver(() => updateSpan());
    ro.observe(img);
    window.addEventListener('resize', updateSpan);
    updateSpan();

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateSpan);
    };
  }, [updateSpan]);

  return (
    <Tile type="button" onClick={onClick} aria-label={title} $rowSpan={rowSpan}>
      <Thumb>
        <Img ref={imgRef} src={assetUrl(imageUrl)} alt="" onLoad={updateSpan} />
        <Overlay aria-hidden="true">
          <OverlayTitle>{title}</OverlayTitle>
        </Overlay>
      </Thumb>
    </Tile>
  );
}
